import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Search } from 'lucide-react';
import {
  PORTAL_MODULES,
  PORTAL_MODULE_GROUP_LABELS,
  canAccessPortalModule,
  filterPortalShortcuts,
  type PortalModule,
  type PortalModuleGroup,
} from '@/constants/portalModules';
import { useAuthStore } from '@/stores/authStore';
import type { AuthUser } from '@/types';
import { cn } from '@/lib/utils';
import { PortalTopbar } from '@/components/portal/PortalTopbar';

const GROUP_ORDER: PortalModuleGroup[] = ['operation', 'growth', 'management'];

const statusLabel = {
  available: 'Ativo',
  soon: 'Em breve',
} as const;

const normalize = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export const PortalPage: React.FC = () => {
  const { user } = useAuthStore();
  const [search, setSearch] = useState('');

  const visibleModules = useMemo(() => {
    const query = normalize(search.trim());

    return PORTAL_MODULES.filter((module) => {
      if (!canAccessPortalModule(user, module)) return false;
      if (!query) return true;

      return normalize(`${module.title} ${module.description}`).includes(query);
    });
  }, [search, user]);

  const groupedModules = useMemo(
    () =>
      GROUP_ORDER.map((group) => ({
        group,
        modules: visibleModules.filter((module) => module.group === group),
      })).filter(({ modules }) => modules.length > 0),
    [visibleModules]
  );

  return (
    <>
      <Helmet>
        <title>PortalFly Zeh Motoca</title>
      </Helmet>

      <div className="min-h-screen bg-background text-foreground">
        <PortalTopbar />

        <main className="mx-auto w-full max-w-[1200px] px-4 py-6 sm:px-5">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-sm font-medium text-muted-foreground">Módulos</h2>
            <div className="relative w-full sm:max-w-xs">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar…"
                aria-label="Buscar módulo"
                className={cn(
                  'h-8 w-full rounded-md border border-input bg-background pl-8 pr-2.5 text-sm',
                  'text-foreground placeholder:text-muted-foreground',
                  'focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/40',
                  'dark:focus:border-amber-400'
                )}
              />
            </div>
          </div>

          {groupedModules.length > 0 ? (
            <div className="space-y-7">
              {groupedModules.map(({ group, modules }) => (
                <section key={group}>
                  <h3 className="mb-2.5 text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                    {PORTAL_MODULE_GROUP_LABELS[group]}
                  </h3>
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] justify-center gap-3">
                    {modules.map((module) => (
                      <PortalModuleCard key={module.id} module={module} user={user} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-border bg-card px-4 py-8 text-center text-sm text-muted-foreground">
              Nenhum módulo encontrado. Ajuste a busca ou fale com o administrador.
            </div>
          )}
        </main>
      </div>
    </>
  );
};

interface PortalModuleCardProps {
  module: PortalModule;
  user: AuthUser | null;
}

const PortalModuleCard: React.FC<PortalModuleCardProps> = ({ module, user }) => {
  const Icon = module.icon;
  const shortcuts = filterPortalShortcuts(user, module);
  const isAvailable = module.status === 'available' && Boolean(module.href);

  return (
    <div
      className={cn(
        'flex min-h-[210px] flex-col rounded-md border border-border bg-card p-3 transition-colors',
        isAvailable && 'hover:border-amber-400/50'
      )}
    >
      <div className="flex items-start justify-between gap-1.5">
        <div
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded',
            'bg-amber-100 text-foreground dark:bg-amber-500/20'
          )}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden />
        </div>
        <span className="text-[9px] font-medium uppercase tracking-wider text-muted-foreground">
          {statusLabel[module.status]}
        </span>
      </div>

      <h4 className="mt-2 line-clamp-2 text-left text-xs font-semibold leading-tight text-foreground">
        {module.title}
      </h4>
      <p className="mt-0.5 min-h-8 line-clamp-2 text-left text-[11px] leading-snug text-muted-foreground">
        {module.description}
      </p>

      {shortcuts.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {shortcuts.map((shortcut) => (
            <Link
              key={`${shortcut.label}-${shortcut.href}`}
              to={shortcut.href!}
              className="text-[10px] font-medium text-muted-foreground underline decoration-border underline-offset-2 transition-colors hover:text-foreground hover:decoration-amber-500/70 dark:decoration-border"
            >
              {shortcut.label}
            </Link>
          ))}
        </div>
      )}

      <div className="mt-2.5">
        {isAvailable && module.href ? (
          <Link
            to={module.href}
            className="inline-flex w-full items-center justify-center gap-0.5 rounded bg-button-brand py-1.5 text-xs font-medium text-button-brand-foreground transition-colors hover:opacity-90"
          >
            Abrir
            <ArrowUpRight className="h-3 w-3" strokeWidth={2.5} aria-hidden />
          </Link>
        ) : (
          <div className="w-full cursor-not-allowed rounded border border-dashed border-border bg-muted/40 py-1.5 text-center text-[10px] font-medium text-muted-foreground dark:bg-muted/20">
            Em planejamento
          </div>
        )}
      </div>
    </div>
  );
};
